import React, { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Download, Receipt, Analytics as AnalyticsIcon, ShowChart as ShowChartIcon, Business as BusinessIcon, Add as AddIcon, LocalHospital as ClinicIcon, Store as StoreIcon, FilterList as FilterListIcon, Refresh as RefreshIcon, EmojiEvents as TrophyIcon, TrendingUp as TrendingUpIcon, LocalFireDepartment as FireIcon, ShoppingCart as CartIcon, AttachMoney as MoneyIcon, Inventory2 as InventoryIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Stack, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { shopModel } from '../Axios/AxiosConfig';

// Types for API response
interface InvoiceItem {
  name: string;
  qty: number;
  priceExclGst: string;
  img?: string;
  imgurl?: string;
  product_image_url?: string;
  image?: string;
}

interface InvoiceSummary {
  subtotal: string;
  deliveryCharge: string;
  sgst: string;
  cgst: string;
  igst: string | null;
  total: string;
  creditsDiscount: number;
  couponDiscount: number;
  totalInWords: string;
  totalItems: number;
}

interface InvoiceData {
  companyid: string;
  companyName: string;
  Service: string;
  orderType?: string;
  date: string;
  from: {
    company: string;
    address: string;
    gst: string;
    mobile: string;
    email: string;
  };
  to: {
    name: string;
    address: string;
    email: string;
    contact: string;
  };
  items: InvoiceItem[];
  summary: InvoiceSummary;
  user: {
    userid: string;
    shopid: string;
    orderid: string;
  };
  invoice: {
    UID: string;
    Invoice: string;
  };
}

interface ApiResponse {
  invoices: InvoiceData[];
}

// Tab panel component
const TabPanel = (props: { children?: React.ReactNode; index: number; value: number }) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
};

// Tab props for accessibility
const a11yProps = (index: number) => ({
  id: `orders-tab-${index}`,
  'aria-controls': `orders-tabpanel-${index}`,
});

const OrdersPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [orders, setOrders] = useState<InvoiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<InvoiceData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [orderToDelete, setOrderToDelete] = useState<{ id: string; uid: string } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [orderTypeTab, setOrderTypeTab] = useState<string>('all');
  const [productImages, setProductImages] = useState<Record<string, string>>({});
  const [loadingImages, setLoadingImages] = useState(false);

  const navigate = useNavigate();

  // Helper for cleaner currency formatting
  const formatCurr = (val: any) => {
    const num = parseFloat(String(val || '').replace(/[^0-9.-]+/g, "")) || 0;
    return num.toLocaleString('en-IN');
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Calculate analytics data
  const calculateAnalytics = (orders: typeof filteredOrders) => {
    const companyRevenue: Record<string, {
      total: number;
      count: number;
      deliveryCharge: number;
    }> = {};

    // Track product sales
    const productSales: Record<string, {
      name: string;
      quantity: number;
      revenue: number;
      orders: number;
      img?: string;
    }> = {};

    let totalRevenue = 0;
    let totalDeliveryCharges = 0;
    const totalOrders = orders.length;

    const safeParse = (val: any) => parseFloat(String(val || '').replace(/[^0-9.-]+/g, "")) || 0;

    orders.forEach(order => {
      const orderTotal = safeParse(order.summary.total);
      const deliveryCharge = safeParse(order.summary.deliveryCharge);

      totalRevenue += orderTotal;
      totalDeliveryCharges += deliveryCharge;

      const company = order.companyName || 'Unknown';
      if (!companyRevenue[company]) {
        companyRevenue[company] = { total: 0, count: 0, deliveryCharge: 0 };
      }
      companyRevenue[company].total += orderTotal;
      companyRevenue[company].deliveryCharge += deliveryCharge;
      companyRevenue[company].count += 1;

      // Track each unique product per order (for correct order-count)
      const seenInThisOrder = new Set<string>();
      order.items.forEach(item => {
        const productName = item.name.trim();
        const quantity = item.qty;
        const itemRevenue = quantity * safeParse(item.priceExclGst);
        const itemImg = item.img || item.imgurl || item.product_image_url || item.image;

        if (!productSales[productName]) {
          productSales[productName] = {
            name: productName,
            quantity: 0,
            revenue: 0,
            orders: 0,
            img: itemImg
          };
        } else {
          if (itemImg && !productSales[productName].img) {
            productSales[productName].img = itemImg;
          }
        }
        productSales[productName].quantity += quantity;
        productSales[productName].revenue += itemRevenue;

        // Count each order once per product (including 100% discount orders)
        if (!seenInThisOrder.has(productName)) {
          productSales[productName].orders += 1;
          seenInThisOrder.add(productName);
        }
      });
    });

    const companyStats = Object.entries(companyRevenue)
      .map(([name, { total, count, deliveryCharge }]) => {
        return {
          name,
          total: parseFloat(total.toFixed(2)),
          orderTotal: parseFloat((total - deliveryCharge).toFixed(2)),
          deliveryCharge: parseFloat(deliveryCharge.toFixed(2)),
          count,
          avgOrderValue: count > 0 ? parseFloat((total / count).toFixed(2)) : 0
        };
      })
      .sort((a, b) => b.total - a.total);

    // Sort by order frequency (most-ordered first)
    const productStats = Object.values(productSales)
      .map(product => ({
        ...product,
        revenue: parseFloat(product.revenue.toFixed(2)),
        avgPrice: product.quantity > 0 ? parseFloat((product.revenue / product.quantity).toFixed(2)) : 0
      }))
      .sort((a, b) => b.orders - a.orders);

    return {
      totalRevenue: parseFloat(totalRevenue.toFixed(2)),
      orderTotal: parseFloat((totalRevenue - totalDeliveryCharges).toFixed(2)),
      deliveryCharges: parseFloat(totalDeliveryCharges.toFixed(2)),
      totalOrders,
      avgOrderValue: totalOrders > 0 ? parseFloat((totalRevenue / totalOrders).toFixed(2)) : 0,
      companyStats,
      productStats
    };
  };


  // Fetch orders from API
  const fetchOrders = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await shopModel.post('/get-invoices', {
        startDate: format(startDate, 'dd/MM/yyyy'),
        endDate: format(endDate, 'dd/MM/yyyy'),
      });

      const result: ApiResponse = response.data;
      setOrders(result.invoices || []);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      // Check if the error is due to no orders found (e.g., 404 Not Found)
      if (error?.response?.status === 404 || error?.response?.data?.message?.toLowerCase().includes('not found')) {
        setOrders([]);
        setError(null);
      } else {
        setOrders([]);
        setError('Failed to fetch orders. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };


  // Fetch orders when component mounts or dates change
  useEffect(() => {
    fetchOrders();
  }, [startDate, endDate]);

  // Fetch product images from inventory API and build a name→imageUrl map
  useEffect(() => {
    if (orders.length === 0) return;
    const fetchProductImages = async () => {
      try {
        const res = await shopModel.post('/getProductbytype', { type: 'All', category: '', companyId: '' });
        const allProducts: any[] = Array.isArray(res?.data) ? res.data : [];
        const imgMap: Record<string, string> = {};
        allProducts.forEach((p: any) => {
          const name = p.name || p.general?.name;
          const img = p.imgurl || p.product_image_url || p.general?.imgurl || p.general?.product_image_url
            || (Array.isArray(p.images) && p.images[0]?.src)
            || (Array.isArray(p.general?.images) && p.general.images[0]?.src);
          if (name && img) imgMap[name] = img;
        });
        setProductImages(imgMap);
      } catch (_) { }
    };
    fetchProductImages();
  }, [orders]);

  const handleDownloadInvoice = (invoiceUrl: string) => {
    window.open(invoiceUrl, '_blank');
  };

  const handleGenerateInvoice = async (shopId: string) => {
    try {
      setError(null);
      setSuccess(null);
      setGeneratingId(shopId);

      const requestOptions: RequestInit = {
        method: 'GET',
        redirect: 'follow'
      };

      const INVOICE_API_URL = process.env.REACT_APP_INVOICE_API_URL;
      if (!INVOICE_API_URL) {
        setError('Invoice API URL is not configured. Set REACT_APP_INVOICE_API_URL in .env');
        return;
      }
      const base = INVOICE_API_URL.replace(/\/$/, '');
      const url = `${base}/${shopId}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 1 minute timeout

      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const text = await response.text();
      let result: any;
      try {
        result = text ? JSON.parse(text) : {};
      } catch (e) {
        result = { error: 'Invalid response from server' };
      }
      if (!response.ok || (result && result.error)) {
        throw new Error(result?.error || `HTTP error! status: ${response.status}`);
      }
      setSuccess(result?.message || 'Invoice generated successfully');

      // Refresh orders to reflect the new invoice
      await fetchOrders();
    } catch (e: any) {
      console.error('Error generating invoice:', e);
      setError(e?.message || 'Failed to generate invoice. Please try again.');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDeleteOrder = (id: string, uid: string) => {
    setOrderToDelete({ id, uid });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!orderToDelete) return;
    try {
      await shopModel.post('/api/v1/delete-order', { id: orderToDelete.id, UID: orderToDelete.uid });
      toast.success("Order deleted successfully!");
      fetchOrders();
    } catch (err: any) {
      console.error("Error deleting order:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to delete order";
      toast.error(errMsg);
    } finally {
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'orderId',
      headerName: 'Order ID',
      flex: 1.2,
      valueGetter: (params) => params.row.user.orderid
    },
    {
      field: 'customerName',
      headerName: 'Customer',
      flex: 1,
      valueGetter: (params) => params.row.to.name
    },
    {
      field: 'customerEmail',
      headerName: 'Email',
      flex: 1.2,
      valueGetter: (params) => params.row.to.email
    },
    {
      field: 'orderDate',
      headerName: 'Order Date',
      flex: 1,
      valueGetter: (params) => params.row.date
    },
    {
      field: 'amount',
      headerName: 'Total Amount',
      flex: 1.2,
      renderCell: (params) => {
        const orderTotal = parseFloat(params.row.summary.total) || 0;
        const deliveryCharge = parseFloat(params.row.summary.deliveryCharge) || 0;
        const totalWithDelivery = (orderTotal + deliveryCharge);

        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {'\u20B9'}{totalWithDelivery.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              (Order: {'\u20B9'}{orderTotal.toLocaleString('en-IN')} + Delivery: {'\u20B9'}{deliveryCharge.toLocaleString('en-IN')})
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      valueGetter: (params) => params.row.companyName
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 0.8,
      valueGetter: (params) => params.row.Service
    },
    {
      field: 'orderCategory',
      headerName: 'Category',
      flex: 0.8,
      renderCell: (params) => {
        const order = params.row;
        let isClinic = false;

        if (order.orderType === 'Clinic' || order.orderType === 'Clinical') {
          isClinic = true;
        } else if (order.orderType === 'Commercial') {
          isClinic = false;
        } else {
          const subtotal = parseFloat(order.summary.subtotal) || 0;
          const total = parseFloat(order.summary.total) || 0;
          const discount = (Number(order.summary.couponDiscount) || 0) + (Number(order.summary.creditsDiscount) || 0);
          if ((subtotal > 0 && discount >= subtotal) || total === 0) isClinic = true;
        }

        return (
          <Chip
            label={isClinic ? 'Clinic' : 'Commercial'}
            color={isClinic ? 'secondary' : 'primary'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Invoice',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const order = params.row;
        const hasInvoice = order.invoice && order.invoice.Invoice;

        return hasInvoice ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() => handleDownloadInvoice(order.invoice.Invoice)}
            color="primary"
          >
            Download
          </Button>
        ) : (
          (() => {
            const isGenerating = generatingId === order.user.shopid;
            return (
              <Button
                variant="contained"
                size="small"
                color="secondary"
                startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <Receipt />}
                onClick={() => handleGenerateInvoice(order.user.shopid)}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating\u2026' : 'Generate'}
              </Button>
            );
          })()
        );
      },
    },
    {
      field: 'delete',
      headerName: 'Delete',
      sortable: false,
      flex: 0.6,
      renderCell: (params) => {
        const order = params.row;
        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation(); // Prevent opening the order details modal
              handleDeleteOrder(order.user.shopid, order.user.userid);
            }}
            sx={{ color: '#483934', '&:hover': { color: '#3d2f2b' } }}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        );
      }
    },
  ];

  // Transform API data for DataGrid (add unique id field)
  const transformedOrders = orders.map((order, index) => ({
    ...order,
    id: order.user.orderid || index, // DataGrid requires unique id field
  }));

  // Unique companies for filter options
  const uniqueCompanies = Array.from(new Set(transformedOrders.map(o => o.companyName))).filter(Boolean).sort();

  const filteredOrders = transformedOrders.filter((order) => {
    const matchesStatus = statusFilter === 'all' || order.Service === statusFilter;
    const matchesCompany = companyFilter === 'all' || order.companyName === companyFilter;

    // Determine category based on 100% discount as requested
    const determineOrderType = (o: InvoiceData) => {
      if (o.orderType === 'Clinic' || o.orderType === 'Clinical') return 'Clinic';
      if (o.orderType === 'Commercial') return 'Commercial';

      const safeParse = (val: any) => parseFloat(String(val || '').replace(/[^0-9.-]+/g, "")) || 0;
      const subtotal = safeParse(o.summary.subtotal);
      const total = safeParse(o.summary.total);
      const discount = safeParse(o.summary.couponDiscount) + safeParse(o.summary.creditsDiscount);

      // If 100% discount or free order
      if ((subtotal > 0 && discount >= subtotal) || total === 0) return 'Clinic';

      return 'Commercial';
    };

    const orderCategory = determineOrderType(order);

    const matchesOrderType =
      orderTypeTab === 'all' ||
      (orderTypeTab === 'Clinic' && orderCategory === 'Clinic') ||
      (orderTypeTab === 'Commercial' && orderCategory === 'Commercial');

    return matchesStatus && matchesCompany && matchesOrderType;
  })
    // Sort latest first by date
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Keep dialog order in sync after refresh so actions update in-place
  useEffect(() => {
    if (!detailOpen || !selectedOrder) return;
    const updated = transformedOrders.find(
      (o) => o.user.shopid === selectedOrder.user.shopid
    );
    if (updated) setSelectedOrder(updated);
  }, [transformedOrders, detailOpen]);

  // Calculate analytics based on filtered orders
  const analytics = calculateAnalytics(filteredOrders);

  return (
    <Box sx={{ px: 0, bgcolor: '#faf6ee', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #3d2f2b 0%, #483934 60%, #5c4840 100%)',
          color: 'white',
          px: 3,
          py: 2.5,
          mb: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} letterSpacing={0.5}>
            Orders
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.3 }}>
            Manage and track all clinic & commercial orders
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Chip
            icon={<ClinicIcon sx={{ color: 'white !important', fontSize: 16 }} />}
            label={`${filteredOrders.filter(o => {
              const s = parseFloat(o.summary.subtotal) || 0; const t = parseFloat(o.summary.total) || 0; const d = (Number(o.summary.couponDiscount) || 0) + (Number(o.summary.creditsDiscount) || 0);
              return o.orderType === 'Clinic' || o.orderType === 'Clinical' || (s > 0 && d >= s) || t === 0;
            }).length} Clinic`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)' }}
          />
          <Chip
            icon={<StoreIcon sx={{ color: 'white !important', fontSize: 16 }} />}
            label={`${filteredOrders.filter(o => {
              const s = parseFloat(o.summary.subtotal) || 0; const t = parseFloat(o.summary.total) || 0; const d = (Number(o.summary.couponDiscount) || 0) + (Number(o.summary.creditsDiscount) || 0);
              return o.orderType === 'Commercial' || (!(o.orderType === 'Clinic' || o.orderType === 'Clinical') && !((s > 0 && d >= s) || t === 0));
            }).length} Commercial`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, border: '1px solid rgba(255,255,255,0.3)' }}
          />
        </Box>
      </Box>

      {/* Main Tabs (Orders / Analytics) */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e8eaf0', px: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="orders tabs"
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { height: 3, borderRadius: 2, backgroundColor: '#483934' } }}
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48, color: '#666' },
            '& .Mui-selected': { color: '#483934' },
          }}
        >
          <Tab icon={<Receipt sx={{ fontSize: 18 }} />} iconPosition="start" label="Orders" {...a11yProps(0)} />
          <Tab icon={<AnalyticsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Analytics" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Category Sub-tabs */}
        <Box sx={{ bgcolor: 'white', px: 2, pt: 1.5, borderBottom: '1px solid #d9cbb4' }}>
          <Tabs
            value={orderTypeTab}
            onChange={(e, v) => setOrderTypeTab(v)}
            aria-label="order type filter tabs"
            TabIndicatorProps={{ style: { height: 3, borderRadius: 2, backgroundColor: '#483934' } }}
            sx={{
              '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 42, color: '#888', px: 2 },
              '& .Mui-selected': { color: '#483934' },
            }}
          >
            <Tab label="All Orders" value="all" />
            <Tab
              icon={<ClinicIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label="Clinic Orders"
              value="Clinic"
            />
            <Tab
              icon={<StoreIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label="Commercial Orders"
              value="Commercial"
            />
          </Tabs>
        </Box>

        {/* Filters Card */}
        <Box sx={{ bgcolor: '#f4ead6', px: 2, py: 2, borderBottom: '1px solid #e2e5f1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <FilterListIcon sx={{ fontSize: 18, color: '#483934' }} />
            <Typography variant="caption" fontWeight={700} color="#483934" letterSpacing={1} sx={{ textTransform: 'uppercase' }}>
              Filters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{ flex: '1 1 210px', minWidth: 200 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: 'white', borderRadius: 1 } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 210px', minWidth: 200 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: 'white', borderRadius: 1 } } }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select
                  value={statusFilter}
                  label="Service"
                  onChange={(e) => setStatusFilter(e.target.value as string)}
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="all">All Services</MenuItem>
                  <MenuItem value="Shop">Shop</MenuItem>
                  <MenuItem value="Consultation">Consultation</MenuItem>
                  <MenuItem value="Delivery">Delivery</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Company</InputLabel>
                <Select
                  value={companyFilter}
                  label="Company"
                  onChange={(e) => setCompanyFilter(e.target.value as string)}
                  sx={{ bgcolor: 'white' }}
                >
                  <MenuItem value="all">All Companies</MenuItem>
                  {uniqueCompanies.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1.5, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate('/orders/create-offline')}
                sx={{ borderColor: '#483934', color: '#483934', '&:hover': { bgcolor: '#f4ead6', borderColor: '#483934' } }}
              >
                Create Offline Order
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchOrders}
                disabled={loading}
                sx={{ bgcolor: '#483934', '&:hover': { bgcolor: '#5c4840' } }}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>
          </Box>
        </Box>      </TabPanel>      <TabPanel value={tabValue} index={1}>
        <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
          {/* KPI Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {/* Card 1: Total Revenue */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '5px solid #483934',
                borderRadius: 3,
                p: 2.5,
                boxShadow: '0 4px 20px rgba(72,57,52,0.04)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>Total Revenue</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main', mt: 0.5 }}>₹{analytics.totalRevenue.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(72,57,52,0.08)', color: 'primary.main', width: 44, height: 44 }}>
                    <TrendingUpIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Orders: ₹{analytics.orderTotal.toLocaleString('en-IN')}</Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Del: ₹{analytics.deliveryCharges.toLocaleString('en-IN')}</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Card 2: Total Orders */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '5px solid #BED040',
                borderRadius: 3,
                p: 2.5,
                boxShadow: '0 4px 20px rgba(72,57,52,0.04)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>Total Orders</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main', mt: 0.5 }}>{analytics.totalOrders}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(190,208,64,0.12)', color: 'secondary.main', width: 44, height: 44 }}>
                    <CartIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                </Box>
                <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>in selected period</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Card 3: Avg Order Value */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '5px solid #483934',
                borderRadius: 3,
                p: 2.5,
                boxShadow: '0 4px 20px rgba(72,57,52,0.04)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>Avg Order Value</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main', mt: 0.5 }}>₹{analytics.avgOrderValue.toLocaleString('en-IN')}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(72,57,52,0.08)', color: 'primary.main', width: 44, height: 44 }}>
                    <ShowChartIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                </Box>
                <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>per order</Typography>
                </Box>
              </Box>
            </Grid>

            {/* Card 4: Unique Products */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '5px solid #BED040',
                borderRadius: 3,
                p: 2.5,
                boxShadow: '0 4px 20px rgba(72,57,52,0.04)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 700 }}>Unique Products</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: 'primary.main', mt: 0.5 }}>{analytics.productStats.length}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(190,208,64,0.12)', color: 'secondary.main', width: 44, height: 44 }}>
                    <InventoryIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                </Box>
                <Box sx={{ mt: 2, borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>sold in period</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Top 3 Ordered Products */}
          {analytics.productStats.length >= 3 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrophyIcon sx={{ color: 'primary.main', fontSize: 26 }} />
                <Typography variant="h6" fontWeight={700} color="primary.main">Top 3 Repeatedly Ordered Products</Typography>
              </Box>
              <Grid container spacing={3} alignItems="stretch">
                {[0, 1, 2].map((rank) => {
                  const p = analytics.productStats[rank];
                  if (!p) return null;
                  const medals = ['🥇', '🥈', '🥉'];
                  const borders = [
                    '3px solid #BED040',
                    '3px solid #483934',
                    '3px solid #7c5c52',
                  ];
                  const shadowColors = [
                    'rgba(190,208,64,0.12)',
                    'rgba(72,57,52,0.08)',
                    'rgba(124,92,82,0.06)',
                  ];
                  const border = borders[rank];
                  const shadow = shadowColors[rank];
                  const displayRank = rank;
                  const imgUrl = p.img || productImages[p.name];

                  return (
                    <Grid item xs={12} sm={4} key={rank}>
                      <Box sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 4,
                        p: 3,
                        height: 250,
                        boxShadow: `0 8px 24px ${shadow}`,
                        border: border,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ position: 'absolute', top: 12, right: 16, fontSize: 32 }}>{medals[displayRank]}</Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                            #{displayRank + 1} MOST ORDERED
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, mb: 1 }}>
                            <Avatar
                              src={imgUrl}
                              variant="rounded"
                              sx={{
                                width: 52,
                                height: 52,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.default',
                                color: 'primary.main'
                              }}
                            >
                              <InventoryIcon sx={{ fontSize: 24 }} />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'primary.main', lineHeight: 1.2 }}>
                              {p.name}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ bgcolor: 'background.default', borderRadius: 1.5, px: 1.2, py: 0.5, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>Orders: {p.orders}</Typography>
                            </Box>
                            <Box sx={{ bgcolor: 'background.default', borderRadius: 1.5, px: 1.2, py: 0.5, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>Qty Sold: {p.quantity}</Typography>
                            </Box>
                          </Box>
                          <Typography variant="h6" fontWeight={800} sx={{ color: 'primary.main', mt: 1.5 }}>
                            ₹{p.revenue.toLocaleString('en-IN')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Total Revenue</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Frequently Bought Products */}
          {analytics.productStats.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FireIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                <Typography variant="h6" fontWeight={700} color="primary.main">Frequently Bought Items</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>by quantity sold</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {[...analytics.productStats].sort((a, b) => b.quantity - a.quantity).slice(0, 8).map((p, i) => {
                  const isTop3 = i < 3;
                  return (
                    <Box key={p.name} sx={{
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: isTop3 ? 'secondary.main' : 'divider',
                      borderRadius: 3,
                      px: 2.5,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      boxShadow: isTop3 ? '0 4px 12px rgba(190,208,64,0.15)' : 'none'
                    }}>
                      <Avatar
                        src={p.img || productImages[p.name]}
                        variant="rounded"
                        sx={{ width: 32, height: 32, bgcolor: 'background.default', color: 'primary.main' }}
                      >
                        <InventoryIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'primary.main' }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{p.quantity} units sold</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Enhanced Product Sales Table */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #483934 0%, #7c5c52 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InventoryIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                  <Typography variant="h6" fontWeight={700} color="white">All Product Sales</Typography>
                  <Chip label={`${analytics.productStats.length} products`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', ml: 'auto', fontWeight: 600 }} />
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'background.default' }}>
                        {['#', 'Product', 'Qty Sold', 'Orders', 'Avg. Price', 'Revenue'].map(h => (
                          <TableCell key={h} align={h === '#' || h === 'Product' ? 'left' : 'right'} sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.73rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.productStats.length > 0 ? analytics.productStats.map((product, index) => {
                        const totalRev = analytics.productStats.reduce((s, p) => s + p.revenue, 0);

                        const borderColor = index === 0 ? 'secondary.main' : index === 1 ? 'primary.main' : 'primary.light';
                        return (
                          <TableRow key={index} hover sx={{ '&:hover': { bgcolor: 'background.default' }, borderLeft: `4px solid`, borderLeftColor: index < 3 ? borderColor : 'transparent' }}>
                            <TableCell>
                              <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: index < 3 ? 'background.default' : 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" fontWeight={800} sx={{ color: 'primary.main' }}>{index + 1}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  src={product.img || productImages[product.name]}
                                  variant="rounded"
                                  sx={{ width: 36, height: 36, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', color: 'primary.main' }}
                                >
                                  <InventoryIcon sx={{ fontSize: 18 }} />
                                </Avatar>
                                <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                                  {product.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Chip label={product.quantity} size="small" sx={{ bgcolor: 'background.default', color: 'primary.main', fontWeight: 700 }} /></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={600} color="primary.main">{product.orders}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" color="text.secondary">₹{product.avgPrice.toLocaleString('en-IN')}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">₹{product.revenue.toLocaleString('en-IN')}</Typography></TableCell>

                          </TableRow>
                        );
                      }) : (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No product data available</TableCell></TableRow>
                      )}
                    </TableBody>
                    {analytics.productStats.length > 0 && (
                      <TableFooter>
                        <TableRow sx={{ bgcolor: 'background.default' }}>
                          <TableCell colSpan={2}><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>{analytics.productStats.reduce((s, p) => s + p.quantity, 0)}</strong></TableCell>
                          <TableCell align="right"><strong>{analytics.productStats.reduce((s, p) => s + p.orders, 0)}</strong></TableCell>
                          <TableCell align="right">—</TableCell>
                          <TableCell align="right"><strong>₹{analytics.productStats.reduce((s, p) => s + p.revenue, 0).toLocaleString('en-IN')}</strong></TableCell>
                          <TableCell align="right"><strong>100%</strong></TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Enhanced Company Revenue Table */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #483934 0%, #7c5c52 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BusinessIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                  <Typography variant="h6" fontWeight={700} color="white">Revenue by Company</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'background.default' }}>
                        {['Company', 'Orders', 'Order Total', 'Delivery', 'Total Revenue', 'Avg. Order'].map(h => (
                          <TableCell key={h} align={h === 'Company' ? 'left' : 'right'} sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.73rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.companyStats.map((company, ci) => {
                        const pct = analytics.totalRevenue > 0 ? (company.total / analytics.totalRevenue) * 100 : 0;
                        return (
                          <TableRow key={company.name} hover sx={{ '&:hover': { bgcolor: 'background.default' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: ci === 0 ? 'secondary.main' : ci === 1 ? 'primary.main' : 'primary.light' }} />
                                <Typography variant="body2" fontWeight={700} color="primary.main">{company.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right"><Chip label={company.count} size="small" sx={{ bgcolor: 'background.default', color: 'primary.main', fontWeight: 700 }} /></TableCell>
                            <TableCell align="right"><Typography variant="body2" color="primary.main">₹{company.orderTotal.toLocaleString('en-IN')}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" color="text.secondary">₹{company.deliveryCharge.toLocaleString('en-IN')}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" fontWeight={700} color="primary.main">₹{company.total.toLocaleString('en-IN')}</Typography></TableCell>
                            <TableCell align="right"><Typography variant="body2" color="primary.main">₹{company.avgOrderValue.toLocaleString('en-IN')}</Typography></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </TabPanel>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mx: 2, mt: 1, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 2, py: 1.5, bgcolor: '#faf6ee', borderBottom: '1px solid #d9cbb4' }}>
        <Box sx={{ bgcolor: '#F4ECD6', borderRadius: 2, px: 2.5, py: 1.2, minWidth: 110, textAlign: 'center', border: '1px solid #d4c5b0' }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">{filteredOrders.length}</Typography>
          <Typography variant="caption" color="primary.main" fontWeight={600}>Total Orders</Typography>
        </Box>
        <Box sx={{ bgcolor: '#f7f1e6', borderRadius: 2, px: 2.5, py: 1.2, minWidth: 110, textAlign: 'center', border: '1px solid #d9cbb4' }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {filteredOrders.filter(o => {
              const safeParse = (val: any) => parseFloat(String(val || '').replace(/[^0-9.-]+/g, "")) || 0;
              const s = safeParse(o.summary.subtotal), t = safeParse(o.summary.total), d = safeParse(o.summary.couponDiscount) + safeParse(o.summary.creditsDiscount);
              return o.orderType === 'Clinic' || o.orderType === 'Clinical' || (s > 0 && d >= s) || t === 0;
            }).length}
          </Typography>
          <Typography variant="caption" color="primary.main" fontWeight={600}>Clinic</Typography>
        </Box>
        <Box sx={{ bgcolor: '#eee8d8', borderRadius: 2, px: 2.5, py: 1.2, minWidth: 110, textAlign: 'center', border: '1px solid #d4c5b0' }}>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {filteredOrders.filter(o => {
              const s = parseFloat(o.summary.subtotal) || 0, t = parseFloat(o.summary.total) || 0, d = (Number(o.summary.couponDiscount) || 0) + (Number(o.summary.creditsDiscount) || 0);
              return o.orderType === 'Commercial' || (!(o.orderType === 'Clinic' || o.orderType === 'Clinical') && !((s > 0 && d >= s) || t === 0));
            }).length}
          </Typography>
          <Typography variant="caption" color="primary.main" fontWeight={600}>Commercial</Typography>
        </Box>
        <Box sx={{ bgcolor: '#edf5c8', borderRadius: 2, px: 2.5, py: 1.2, minWidth: 150, textAlign: 'center', border: '1px solid #c8d88a' }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#5a7a1a' }}>
            {'\u20B9'}{filteredOrders.reduce((s, o) => {
              const safeParse = (val: any) => parseFloat(String(val || '').replace(/[^0-9.-]+/g, "")) || 0;
              return s + safeParse(o.summary.total) + safeParse(o.summary.deliveryCharge);
            }, 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </Typography>
          <Typography variant="caption" fontWeight={600} sx={{ color: '#5a7a1a' }}>Total Revenue</Typography>
        </Box>
      </Box>

      <Box sx={{ bgcolor: 'white', mx: 0, borderRadius: 0, boxShadow: 'none' }}>
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <DataGrid
            rows={filteredOrders}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            rowHeight={58}
            disableRowSelectionOnClick
            onRowClick={(params) => {
              const row = params.row as InvoiceData;
              setSelectedOrder(row);
              setDetailOpen(true);
            }}
            loading={loading}
            sx={{
              opacity: loading ? 0.6 : 1,
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f4ead6',
                color: '#483934',
                fontWeight: 700,
                fontSize: '0.78rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderBottom: '2px solid #c5cae9',
              },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f8',
                fontSize: '0.85rem',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f7f1e6',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#fdf9f2',
              },
              '& .MuiDataGrid-overlay': {
                backgroundColor: 'rgba(255,255,255,0.85)',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e8eaf0',
                bgcolor: '#f9f5ed',
              },
            }}
          />
        </Box>
      </Box>

      {/* Order Details Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #3d2f2b 0%, #483934 100%)',
            color: 'white',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Order Details</Typography>
              {selectedOrder && (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>#{selectedOrder.user.orderid}</Typography>
              )}
            </Box>
            <IconButton aria-label="close" onClick={() => setDetailOpen(false)} sx={{ color: 'white' }}>
              {'\u2715'}
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#faf6ee', p: 3 }}>
          {selectedOrder && (
            <Box>
              {/* Order meta chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                <Chip icon={<Receipt sx={{ fontSize: 14 }} />} label={`Order: ${selectedOrder.user.orderid}`} size="small" sx={{ bgcolor: '#f4ead6', color: '#483934', fontWeight: 600 }} />
                <Chip label={`Date: ${selectedOrder.date}`} size="small" sx={{ bgcolor: '#eee8d8', color: '#6b5e52', fontWeight: 600 }} />
                <Chip label={`Company: ${selectedOrder.companyName}`} size="small" sx={{ bgcolor: '#fce4ec', color: '#5c4840', fontWeight: 600 }} />
                <Chip label={selectedOrder.Service} size="small" sx={{ bgcolor: '#eaf5d0', color: '#5a7a1a', fontWeight: 600 }} />
              </Box>

              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e8eaf0' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#483934" mb={1}>From</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedOrder.from.company}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedOrder.from.address}</Typography>
                    <Typography variant="body2" color="text.secondary">GST: {selectedOrder.from.gst}</Typography>
                    <Typography variant="body2" color="text.secondary">Email: {selectedOrder.from.email}</Typography>
                    <Typography variant="body2" color="text.secondary">Mobile: {selectedOrder.from.mobile}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e8eaf0' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#483934" mb={1}>To</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedOrder.to.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedOrder.to.address}</Typography>
                    <Typography variant="body2" color="text.secondary">Email: {selectedOrder.to.email}</Typography>
                    <Typography variant="body2" color="text.secondary">Contact: {selectedOrder.to.contact}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} color="#483934" sx={{ mb: 1 }}>Items</Typography>
              <TableContainer sx={{ maxHeight: 280, bgcolor: 'white', borderRadius: 2, border: '1px solid #e8eaf0' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f4ead6', color: '#483934', fontWeight: 700 }}>Product</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f4ead6', color: '#483934', fontWeight: 700 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f4ead6', color: '#483934', fontWeight: 700 }}>Price (excl GST)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items.map((it, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={productImages[it.name]}
                            variant="rounded"
                            sx={{ width: 36, height: 36, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', color: 'primary.main' }}
                          >
                            <InventoryIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography variant="body2" fontWeight={600} color="primary.main">{it.name}</Typography>
                        </TableCell>
                        <TableCell align="right">{it.qty}</TableCell>
                        <TableCell align="right">₹{formatCurr(it.priceExclGst)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e8eaf0' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#483934" mb={1}>Summary</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Subtotal</Typography><Typography variant="body2">{'\u20B9'}{formatCurr(selectedOrder.summary.subtotal)}</Typography></Box>
                    {Number(selectedOrder.summary.deliveryCharge) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Delivery</Typography><Typography variant="body2">{'\u20B9'}{formatCurr(selectedOrder.summary.deliveryCharge)}</Typography></Box>
                    )}
                    {Number(selectedOrder.summary.creditsDiscount) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Credits Discount</Typography><Typography variant="body2" color="error.main">-{'\u20B9'}{formatCurr(selectedOrder.summary.creditsDiscount)}</Typography></Box>
                    )}
                    {Number(selectedOrder.summary.couponDiscount) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Coupon Discount</Typography><Typography variant="body2" color="error.main">-{'\u20B9'}{formatCurr(selectedOrder.summary.couponDiscount)}</Typography></Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">Items</Typography><Typography variant="body2">{selectedOrder.summary.totalItems}</Typography></Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e8eaf0' }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#483934" mb={1}>Tax & Total</Typography>
                    {Number(selectedOrder.summary.sgst) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">SGST</Typography><Typography variant="body2">{'\u20B9'}{formatCurr(selectedOrder.summary.sgst)}</Typography></Box>
                    )}
                    {Number(selectedOrder.summary.cgst) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">CGST</Typography><Typography variant="body2">{'\u20B9'}{formatCurr(selectedOrder.summary.cgst)}</Typography></Box>
                    )}
                    {Number(selectedOrder.summary.igst) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" color="text.secondary">IGST</Typography><Typography variant="body2">{'\u20B9'}{formatCurr(selectedOrder.summary.igst)}</Typography></Box>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight={700} color="#483934">Total</Typography>
                      <Typography variant="h6" fontWeight={800} color="#483934">{'\u20B9'}{formatCurr(selectedOrder.summary.total)}</Typography>
                    </Box>
                    {selectedOrder.summary.totalInWords && String(selectedOrder.summary.totalInWords).trim().length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>{selectedOrder.summary.totalInWords}</Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#faf6ee', borderTop: '1px solid #e8eaf0' }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined" sx={{ borderColor: '#483934', color: '#483934' }}>Close</Button>
          {selectedOrder && !selectedOrder.invoice?.Invoice && (
            (() => {
              const isGenerating = generatingId === selectedOrder.user.shopid;
              return (
                <Button
                  onClick={() => handleGenerateInvoice(selectedOrder.user.shopid)}
                  variant="contained"
                  color="secondary"
                  startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating\u2026' : 'Generate'}
                </Button>
              );
            })()
          )}
          {selectedOrder?.invoice?.Invoice && (
            <Button onClick={() => handleDownloadInvoice(selectedOrder!.invoice!.Invoice)} variant="contained" color="secondary">
              Download Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Deletion Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #3d2f2b 0%, #483934 100%)',
            color: 'white',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="h6" fontWeight={700}>Confirm Delete</Typography>
            <IconButton aria-label="close" onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'white' }}>
              {'\u2715'}
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#faf6ee', p: 3, mt: 2 }}>
          <Typography variant="body1">
            Are you sure you want to delete this order? This action is permanent and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#faf6ee', borderTop: '1px solid #e8eaf0' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" sx={{ borderColor: '#483934', color: '#483934', '&:hover': { borderColor: '#3d2f2b', bgcolor: '#f4ead6' }, borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" sx={{ bgcolor: '#483934', '&:hover': { bgcolor: '#3d2f2b' }, color: 'white', borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersPage;

